import { useCallback, useEffect, useMemo, useState } from 'react'
import { useStableCallback } from './useStableCallback'

interface DismissGuardOptions {
  /** Whether the dialog or panel is open; closing it drops the question too. */
  open: boolean
  /** Edits that closing now would throw away. */
  hasUnsavedChanges: boolean
  /** Closes without keeping the edits. */
  onClose: () => void
}

export interface DismissGuard {
  /**
   * Every way of closing that isn't an explicit Cancel or Discard: a click
   * outside, Escape, the close button. Closes at once when nothing would be
   * lost; otherwise asks.
   */
  requestClose: () => void
  /** The question is showing; the surface leaves Escape and outside clicks to it. */
  isAsking: boolean
  /** Back to the dialog, edits intact. */
  keepEditing: () => void
  /** Closes and throws the edits away. */
  discard: () => void
  /** Closes the question, then keeps the edits the way the surface's own button does. */
  save: (keep: () => void) => void
}

/**
 * A dialog or panel that holds edits asks before a stray click outside,
 * Escape or its close button throws them away (see UnsavedChangesQuestion):
 * keep editing, discard them, or keep them (Save, Apply).
 */
export function useDismissGuard({
  open,
  hasUnsavedChanges,
  onClose,
}: DismissGuardOptions): DismissGuard {
  const [isAsking, setIsAsking] = useState(false)
  const close = useStableCallback(onClose)

  useEffect(() => {
    if (!open) setIsAsking(false)
  }, [open])

  const requestClose = useCallback(() => {
    if (hasUnsavedChanges) setIsAsking(true)
    else close()
  }, [close, hasUnsavedChanges])

  const keepEditing = useCallback(() => setIsAsking(false), [])

  const discard = useCallback(() => {
    setIsAsking(false)
    close()
  }, [close])

  const save = useCallback((keep: () => void) => {
    setIsAsking(false)
    keep()
  }, [])

  const asking = open && isAsking
  return useMemo(
    () => ({ requestClose, isAsking: asking, keepEditing, discard, save }),
    [asking, discard, keepEditing, requestClose, save]
  )
}
